import axios, { AxiosResponse } from 'axios';
import Cookies from 'universal-cookie';

import {
  IWheelOfFortuneBatchSpinResponse,
  IWheelOfFortuneHistoryResponse,
  IWheelOfFortuneResponse,
  IWheelOfFortuneSpinResponse,
  WheelKind,
} from 'models/wheel-of-fortune/i-wheel-of-fortune';

import { CookiesKeys } from 'enums/cookies-keys.enum';

const BASE = '/api/v1/lottery/wheel-of-fortune';

/**
 * The VIP wheel mirrors the regular one under a `/vip/` prefix, so every read and the
 * spin are the same call with a different base. Results and history stay unified across
 * both wheels and are therefore NOT parameterised.
 */
const wheelBase = (kind: WheelKind) => (kind === 'vip' ? `${BASE}/vip` : BASE);

const headers = () => ({
  'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
});

export const getWheelOfFortuneResultsService = (
  signal: AbortSignal
): Promise<AxiosResponse<IWheelOfFortuneSpinResponse[]>> => {
  return axios.get(`${BASE}/results/`, { signal, headers: headers() });
};

export const makeWheelOfFortuneSpinService = (
  next_spin_number: number,
  signal: AbortSignal,
  kind: WheelKind = 'regular'
): Promise<AxiosResponse<IWheelOfFortuneSpinResponse>> => {
  return axios.post(`${wheelBase(kind)}/spin/`, next_spin_number, { signal, headers: headers() });
};

/**
 * Spins `count` times in one request and answers with every result.
 *
 * WHY THE COUNT RIDES IN THE QUERY STRING AND NOT THE BODY — this is load-bearing, do not
 * "tidy" it into a JSON body. The single-spin call above posts `next_spin_number` as a BARE
 * NUMBER: axios leaves a non-object payload untouched and sets no JSON content type, so the
 * browser labels the request `text/plain;charset=UTF-8`. DRF parses the body lazily, on the
 * first access to `request.data` — and it raises `UnsupportedMediaType` (415) for a
 * text/plain body. The moment the view reads a count out of the body, every request from an
 * older deployed bundle 415s. `request.query_params` never touches the parser, so the count
 * is a query parameter and the body stays exactly the bare number it has always been.
 *
 * A separate export rather than an optional argument on the call above: the two answer with
 * two different shapes, and a union return type would push an `in`-check onto every caller
 * of the 1x path. The modal picks the function by the count it is about to spend.
 */
export const makeWheelOfFortuneBatchSpinService = (
  next_spin_number: number,
  signal: AbortSignal,
  kind: WheelKind = 'regular',
  count: number
): Promise<AxiosResponse<IWheelOfFortuneBatchSpinResponse>> => {
  return axios.post(`${wheelBase(kind)}/spin/`, next_spin_number, {
    signal,
    // `params`, not a second body argument — see the note above; the body must stay the
    // bare number so DRF is never asked to parse it.
    params: { count },
    headers: headers(),
  });
};

export const getWheelOfFortuneService = (
  signal: AbortSignal,
  kind: WheelKind = 'regular'
): Promise<AxiosResponse<IWheelOfFortuneResponse>> => {
  return axios.get(`${wheelBase(kind)}/`, { signal, headers: headers() });
};

/**
 * Fetches wheel of fortune history with pagination
 * @param signal - AbortSignal for request cancellation
 * @param page - Page number (default: 1, must be >= 1)
 * @param pageSize - Number of items per page (default: 10, must be > 0)
 * @returns Promise with paginated history response
 */
export const getWheelOfFortuneHistoryService = (
  signal: AbortSignal,
  page: number = 1,
  pageSize: number = 10
): Promise<AxiosResponse<IWheelOfFortuneHistoryResponse>> => {
  const validPage = Math.max(1, page);
  const validPageSize = Math.max(1, pageSize);

  return axios.get(`${BASE}/history/`, {
    signal,
    params: {
      page: validPage,
      page_size: validPageSize,
    },
    headers: headers(),
  });
};
